import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import '../qt-history.css';
import {
  EuiDatePicker,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiLoadingSpinner,
  EuiSelect,
  EuiSpacer,
  EuiText,
  EuiToolTip,
} from '@elastic/eui';
import { loadQTArchiveWithOption } from '../components/QT';
import { TranslateLanguage as translateLanguageText } from './KeywordTranslator';

const toLocalDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatShortDate = date => {
  const [, month, day] = String(date).split('-');
  return month && day ? `${Number(month)}/${Number(day)}` : date;
};

export default function QTPlayerModal({
  initialURL,
  initialDescription,
  initialSource = 'CGNTV',
  YTKey,
  TranslateLanguage,
}) {
  const translate = text => (
    (TranslateLanguage || translateLanguageText)(localStorage.userLanguage, text)
  );
  const [source, setSource] = useState(initialSource);
  const [videos, setVideos] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [url, setUrl] = useState(initialURL || '');
  const [description, setDescription] = useState(initialDescription || '');
  const [loading, setLoading] = useState(false);

  const sourceOptions = [
    { value: 'CGNTV', text: translate('CGNTV 活潑的生命') },
    { value: 'CAI', text: translate('二重行道會 蔡金龍牧師') },
    { value: 'GCH', text: translate('台北榮耀堂（週一至週五）') },
  ];

  const selectVideo = video => {
    if (!video) return;
    setSelectedDate(video.date);
    setUrl(video.url);
    setDescription(video.text || '');
  };

  const fetchBySource = async (nextSource, preferredDate = '', isInitial = false) => {
    try {
      setLoading(true);
      const archive = await loadQTArchiveWithOption(nextSource, YTKey);
      const archiveItems = archive.items || [];
      setVideos(archiveItems);

      const today = toLocalDateKey();
      const preferredVideo = preferredDate
        ? archiveItems.find(item => item.date === preferredDate)
        : null;
      const initialVideo = isInitial && initialURL
        ? archiveItems.find(item => item.url === initialURL)
        : null;
      const todayVideo = archiveItems.find(item => item.date === today);
      // The tile's initial URL may have been loaded on an earlier day while
      // the app stayed open. Prefer today's freshly fetched archive item.
      const selectedVideo = preferredVideo || todayVideo || initialVideo || archiveItems[0];

      if (selectedVideo) {
        selectVideo(selectedVideo);
      } else {
        setSelectedDate('');
        setUrl('');
        setDescription(archive.message || translate('抱歉，沒有找到可播放的內容。'));
      }
    } catch (error) {
      console.error('Failed to load QT archive:', error);
      setVideos([]);
      setSelectedDate('');
      setUrl('');
      setDescription(`<p>${translate('讀取失敗，請稍後再試。')}</p>`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.body.classList.add('qt-player-modal-open');
    return () => document.body.classList.remove('qt-player-modal-open');
  }, []);
  useEffect(() => {
    if (YTKey) fetchBySource(initialSource, '', true);
    // The archive is loaded once when the modal opens; source changes are handled below.
  }, []);

  const iframeSrc = useMemo(() => {
    if (!url) return '';
    const match = url.match(/(?:https?:\/{2})?(?:w{3}\.)?youtu(?:be)?\.(?:com|be)(?:\/watch\?v=|\/)([^\s&]+)/);
    if (!match || !match[1]) return '';
    const parsed = new URL(url, window.location.origin);
    const startTime = parsed.searchParams.get('t');
    return `https://www.youtube.com/embed/${match[1]}?autoplay=1${startTime ? `&start=${startTime}` : ''}`;
  }, [url]);

  const availableDates = useMemo(
    () => new Set(videos.map(video => video.date)),
    [videos]
  );
  const selectedIndex = videos.findIndex(video => video.date === selectedDate);
  const todayVideo = videos.find(video => video.date === toLocalDateKey());
  const olderVideo = selectedIndex >= 0 ? videos[selectedIndex + 1] : null;
  const newerVideo = selectedIndex > 0 ? videos[selectedIndex - 1] : null;
  const oldestDate = videos.length ? videos[videos.length - 1].date : '';
  const newestDate = videos.length ? videos[0].date : '';
  const recentVideos = videos.slice(0, 7);

  return (
    <div className="qt-player-modal-content">
      <EuiFlexGroup
        className="qt-player-source-row"
        justifyContent="center"
        alignItems="center"
        gutterSize="s"
        responsive={false}
      >
        <EuiFlexItem grow={false}>
          <EuiFormRow label={translate('頻道')} display="columnCompressed">
            <EuiSelect
              compressed
              options={sourceOptions}
              value={source}
              onChange={event => {
                const nextSource = event.target.value;
                setSource(nextSource);
                localStorage.qtOption = nextSource;
                fetchBySource(nextSource, selectedDate);
              }}
              aria-label="qt-source-select"
              disabled={loading}
              className="qt-source-select"
            />
          </EuiFormRow>
        </EuiFlexItem>
      </EuiFlexGroup>

      <div className="qt-date-navigation">
        <div className="qt-date-picker-wrap">
          <EuiDatePicker
            selected={selectedDate ? moment(selectedDate, 'YYYY-MM-DD', true) : null}
            onChange={date => {
              const dateKey = date?.isValid() ? date.format('YYYY-MM-DD') : '';
              const selectedVideo = videos.find(video => video.date === dateKey);
              if (selectedVideo) selectVideo(selectedVideo);
            }}
            filterDate={date => availableDates.has(date.format('YYYY-MM-DD'))}
            minDate={oldestDate ? moment(oldestDate, 'YYYY-MM-DD', true) : undefined}
            maxDate={newestDate ? moment(newestDate, 'YYYY-MM-DD', true) : undefined}
            dateFormat="YYYY-MM-DD"
            placeholder={translate('選擇日期')}
            disabled={loading || videos.length === 0}
            className="qt-date-picker"
            compressed
          />
        </div>

        <div className="qt-date-actions">
          <EuiToolTip content={translate('上一篇')}>
            <button
              type="button"
              className="qt-nav-action qt-nav-action--step"
              aria-label={translate('上一篇')}
              onClick={() => selectVideo(olderVideo)}
              disabled={!olderVideo || loading}
            >
              <span aria-hidden="true" className="qt-nav-arrow">←</span>
              <span className="qt-nav-label">{translate('上一篇')}</span>
            </button>
          </EuiToolTip>

          <EuiToolTip content={translate('下一篇')}>
            <button
              type="button"
              className="qt-nav-action qt-nav-action--step"
              aria-label={translate('下一篇')}
              onClick={() => selectVideo(newerVideo)}
              disabled={!newerVideo || loading}
            >
              <span className="qt-nav-label">{translate('下一篇')}</span>
              <span aria-hidden="true" className="qt-nav-arrow">→</span>
            </button>
          </EuiToolTip>

          <EuiToolTip
            content={todayVideo ? translate('播放今天內容') : translate('今天暫無內容')}
          >
            <button
              type="button"
              className="qt-nav-action qt-nav-action--today"
              onClick={() => selectVideo(todayVideo)}
              disabled={!todayVideo || loading}
            >
              {translate('今天')}
            </button>
          </EuiToolTip>
        </div>
      </div>
      <EuiSpacer size="s" />

      {loading ? (
        <div className="qt-player-loading">
          <EuiLoadingSpinner size="xl" />
        </div>
      ) : iframeSrc ? (
        <>
          <div className="qt-player-frame">
            <iframe
              id="ytplayer"
              className="qt-player-iframe"
              title="qt-player"
              src={iframeSrc}
              frameBorder="0"
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
            />
          </div>
          {description && (
            <EuiText size="s" className="qt-current-video-title">
              <p>{translate(description)}</p>
            </EuiText>
          )}
        </>
      ) : (
        <div className="qt-player-message">
          <EuiText>
            <span
              dangerouslySetInnerHTML={{
                __html: description || translate('抱歉，沒有找到可播放的內容。'),
              }}
            />
          </EuiText>
        </div>
      )}

      {!loading && videos.length > 0 && (
        <div className="qt-history-section">
          <EuiText size="s">
            <strong>{translate('最近可播放')}</strong>
            <span className="qt-history-count">{recentVideos.length}</span>
          </EuiText>
          <div className="qt-history-strip" role="list" aria-label={translate('最近可播放')}>
            {recentVideos.map(video => (
              <button
                type="button"
                role="listitem"
                key={`${source}-${video.date}`}
                className={`qt-history-date${video.date === selectedDate ? ' is-selected' : ''}`}
                onClick={() => selectVideo(video)}
                title={video.text}
              >
                {formatShortDate(video.date)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
